// src/pages/PermisosDocumentos.js
import React, { useState, useEffect } from 'react';
import { FaFileAlt, FaUpload, FaPrint, FaTimes, FaSearch, FaHammer, FaHome, FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaExpand } from 'react-icons/fa';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { collection, query, onSnapshot, orderBy, doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './PermisosDocumentos.css';

const PermisosDocumentos = () => {
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    const [tipoPermiso, setTipoPermiso] = useState('construccion'); // 'construccion' o 'techado'
    const [casas, setCasas] = useState([]);
    const [selectedCasa, setSelectedCasa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fraccionamientoData, setFraccionamientoData] = useState(null);
    const [logoUrl, setLogoUrl] = useState('');
    const [firmaUrl, setFirmaUrl] = useState('');
    const [uploadingFirma, setUploadingFirma] = useState(false);
    const [adminNombre, setAdminNombre] = useState('');
    const [adminCargo, setAdminCargo] = useState('Administradora del Fraccionamiento');
    const [direccionFraccionamiento, setDireccionFraccionamiento] = useState('');
    const [ciudadEstado, setCiudadEstado] = useState('San Juan del Río Querétaro');
    const [fechaPermiso, setFechaPermiso] = useState(new Date());
    const [tituloPermiso, setTituloPermiso] = useState('PERMISO DE CONSTRUCCIÓN');
    const [redaccionConstruccion, setRedaccionConstruccion] = useState('');
    const [redaccionTechado, setRedaccionTechado] = useState('');
    const [editorModalOpen, setEditorModalOpen] = useState(false);
    const [fontSize, setFontSize] = useState(12);
    const [fontFamily, setFontFamily] = useState('Times New Roman');
    
    // Para techado: 4 imágenes de referencia
    const [imagenesReferencia, setImagenesReferencia] = useState([null, null, null, null]);
    const [uploadingImagenes, setUploadingImagenes] = useState([false, false, false, false]);

    // Cargar datos del fraccionamiento
    useEffect(() => {
        const loadFraccionamientoData = async () => {
            if (!fraccionamientoId) return;

            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (fraccDoc.exists()) {
                    const data = fraccDoc.data() || {};
                    const configRecibos = data.configuracionRecibos || {};
                    
                    // Obtener logo
                    const logo = configRecibos.logoUrl || data.logoUrl || '';
                    setLogoUrl(logo);
                    
                    // Obtener firma guardada
                    const firma = configRecibos.firmaPermisoUrl || configRecibos.firmaConstanciaUrl || '';
                    setFirmaUrl(firma);
                    
                    // Obtener nombre del fraccionamiento
                    const nombre = configRecibos.condominioNombre || data.condominioNombre || data.nombre || '';
                    
                    // Obtener dirección
                    const direccion = configRecibos.condominioDireccion || data.condominioDireccion || data.direccion || '';
                    setDireccionFraccionamiento(direccion);
                    
                    // Obtener administrador
                    const adminNombreData = configRecibos.administradorNombre || data.adminNombre || data.administradorNombre || '';
                    setAdminNombre(adminNombreData);
                    
                    // Cargar configuración de permisos guardada
                    const configPermiso = configRecibos.configuracionPermiso || {};
                    const configConstruccion = configPermiso.construccion || {};
                    const configTechado = configPermiso.techado || {};
                    
                    if (configConstruccion.redaccion) setRedaccionConstruccion(configConstruccion.redaccion);
                    if (configTechado.redaccion) setRedaccionTechado(configTechado.redaccion);
                    
                    if (configPermiso.fecha) {
                        const fechaGuardada = configPermiso.fecha.toDate ? configPermiso.fecha.toDate() : new Date(configPermiso.fecha);
                        setFechaPermiso(fechaGuardada);
                    }
                    if (configPermiso.adminCargo) setAdminCargo(configPermiso.adminCargo);
                    
                    // Cargar imágenes de referencia guardadas
                    if (configTechado.imagenesReferencia && Array.isArray(configTechado.imagenesReferencia)) {
                        // Asegurar que el array tenga exactamente 4 elementos
                        const imagenesCargadas = [...configTechado.imagenesReferencia];
                        while (imagenesCargadas.length < 4) {
                            imagenesCargadas.push(null);
                        }
                        const imagenesFinales = imagenesCargadas.slice(0, 4);
                        setImagenesReferencia(imagenesFinales);
                        console.log('Imágenes de referencia cargadas desde la configuración:', imagenesFinales.filter(img => img !== null).length, 'imágenes');
                    } else {
                        console.log('No se encontraron imágenes de referencia guardadas');
                    }
                    
                    setFraccionamientoData({
                        nombre,
                        direccion,
                        adminNombre: adminNombreData
                    });
                }
            } catch (error) {
                console.error('Error cargando datos del fraccionamiento:', error);
            }
        };

        loadFraccionamientoData();
    }, [fraccionamientoId]);

    // Actualizar título según tipo de permiso
    useEffect(() => {
        if (tipoPermiso === 'techado') {
            setTituloPermiso('PERMISO DE TECHUMBRE/TECHADO');
        } else {
            setTituloPermiso('PERMISO DE CONSTRUCCIÓN');
        }
        
        // Si hay una casa seleccionada, actualizar la redacción con los datos actuales
        if (selectedCasa && fraccionamientoData) {
            const residenteNombre = getResidentName(selectedCasa);
            const numeroCasa = selectedCasa.numero || 'N/A';
            const nombreFracc = fraccionamientoData.nombre || 'N/A';
            const direccion = direccionFraccionamiento || 'N/A';
            
            if (tipoPermiso === 'construccion' && !redaccionConstruccion.trim()) {
                const redaccionDefault = `Por medio de la presente se le otorga el permiso de construcción al C. ${residenteNombre}, propietario condominio 2 casa ${numeroCasa}, ubicado en ${nombreFracc}, ${direccion}. Dicho permiso es exclusivamente para la construcción permitida en el reglamento protocolizado y vigente para el Condominio ${nombreFracc}, en el Capítulo I; EN RELACIÓN A LAS UNIDADES EN CONDOMINIO, ARTÍCULO 7, PÁRRAFO 5, SEGUNDA PARTE: La única ampliación autorizada es la que corresponde al prototipo denominado Pera (vivienda de 2 recámaras y 2 baños completos) esta solo se podrá llevar a cabo al frente de la vivienda siguiendo los lineamientos del manual de ampliación específico para este prototipo. Los acabados (pintura, terminados, losas, tejas, etc) deberán de ser del mismo tipo de los utilizados en el resto de las casas para no romper con la armonía visual-arquitectónica del Condominio. Con el compromiso de cumplir debidamente con el convenio de pago por cuotas de mantenimiento vencidas.`;
                setRedaccionConstruccion(redaccionDefault);
            } else if (tipoPermiso === 'techado' && !redaccionTechado.trim()) {
                const redaccionDefault = `Por medio de la presente se le otorga el permiso de instalación de techumbre al C. ${residenteNombre}, propietario del condominio 2 casa ${numeroCasa}, ubicado en la Unidad Condominal ${nombreFracc}, ${direccion}. 
Dicho permiso es exclusivamente para la instalación de techumbre permitida establecida en la Asamblea Plenaria celebrada el día 29 de septiembre de 2024, con las especificaciones exactas del prototipo aprobado. 
Los acabados deberán de ser del mismo tipo al prototipo aprobado para no romper con la armonía visual-arquitectónica del Condominio.    
Siendo las siguientes:

TECHUMBRE ESPECIFICACIONES:

MATERIAL
CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"
• PERFIL CUADRADO DE 1" CAL. 14 PARA SOPORTE
• ARMADO DE ESTRUCTURA PARA EL TECHADO, PERFIL PTR 3X2" CAL. 14
• UTILIZACIÓN DE PLASTI TEJA
• ÁNGULOS DE FIJACIÓN AL MURO DE SOPORTE.`;
                setRedaccionTechado(redaccionDefault);
            }
        }
    }, [tipoPermiso, selectedCasa, fraccionamientoData, direccionFraccionamiento]);

    // Cargar todas las casas
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const q = query(casasRef, orderBy('numero'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const casasList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCasas(casasList);
            setLoading(false);
        }, (error) => {
            console.error('Error cargando casas:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    const getResidentName = (casa) => {
        return casa?.nombre_residente || 
               casa?.residente || 
               casa?.usuario || 
               casa?.nombreResidente || 
               (casa?.propietario && casa.propietario.nombre) || 
               (casa?.contacto && casa.contacto.nombre) || 
               'Residente no registrado';
    };

    const filteredCasas = casas.filter(casa => {
        const searchLower = searchTerm.toLowerCase();
        const numero = String(casa.numero || '').toLowerCase();
        const nombre = getResidentName(casa).toLowerCase();
        return numero.includes(searchLower) || nombre.includes(searchLower);
    });

    const handleCasaSelect = (casa) => {
        setSelectedCasa(casa);
        
        // Auto-completar redacción si está vacía al seleccionar una casa
        const residenteNombre = getResidentName(casa);
        const numeroCasa = casa.numero || 'N/A';
        const nombreFracc = fraccionamientoData?.nombre || 'N/A';
        const direccion = direccionFraccionamiento || 'N/A';
        
        if (tipoPermiso === 'construccion' && !redaccionConstruccion.trim()) {
            const redaccionDefault = `Por medio de la presente se le otorga el permiso de construcción al C. ${residenteNombre}, propietario condominio 2 casa ${numeroCasa}, ubicado en ${nombreFracc}, ${direccion}. Dicho permiso es exclusivamente para la construcción permitida en el reglamento protocolizado y vigente para el Condominio ${nombreFracc}, en el Capítulo I; EN RELACIÓN A LAS UNIDADES EN CONDOMINIO, ARTÍCULO 7, PÁRRAFO 5, SEGUNDA PARTE: La única ampliación autorizada es la que corresponde al prototipo denominado Pera (vivienda de 2 recámaras y 2 baños completos) esta solo se podrá llevar a cabo al frente de la vivienda siguiendo los lineamientos del manual de ampliación específico para este prototipo. Los acabados (pintura, terminados, losas, tejas, etc) deberán de ser del mismo tipo de los utilizados en el resto de las casas para no romper con la armonía visual-arquitectónica del Condominio. Con el compromiso de cumplir debidamente con el convenio de pago por cuotas de mantenimiento vencidas.`;
            setRedaccionConstruccion(redaccionDefault);
        } else if (tipoPermiso === 'techado' && !redaccionTechado.trim()) {
            const redaccionDefault = `Por medio de la presente se le otorga el permiso de instalación de techumbre al C. ${residenteNombre}, propietario del condominio 2 casa ${numeroCasa}, ubicado en la Unidad Condominal ${nombreFracc}, ${direccion}. 
Dicho permiso es exclusivamente para la instalación de techumbre permitida establecida en la Asamblea Plenaria celebrada el día 29 de septiembre de 2024, con las especificaciones exactas del prototipo aprobado. 
Los acabados deberán de ser del mismo tipo al prototipo aprobado para no romper con la armonía visual-arquitectónica del Condominio.    
Siendo las siguientes:

TECHUMBRE ESPECIFICACIONES:

MATERIAL
CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"
• PERFIL CUADRADO DE 1" CAL. 14 PARA SOPORTE
• ARMADO DE ESTRUCTURA PARA EL TECHADO, PERFIL PTR 3X2" CAL. 14
• UTILIZACIÓN DE PLASTI TEJA
• ÁNGULOS DE FIJACIÓN AL MURO DE SOPORTE.`;
            setRedaccionTechado(redaccionDefault);
        }
    };

    const handleFirmaUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !fraccionamientoId) return;

        setUploadingFirma(true);
        try {
            const fileRef = ref(storage, `fraccionamientos/${fraccionamientoId}/permisos/firma_${Date.now()}.${file.name.split('.').pop()}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            setFirmaUrl(url);
            
            // Guardar URL en Firestore
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccData = (await getDoc(fraccRef)).data() || {};
            const configRecibos = fraccData.configuracionRecibos || {};
            await updateDoc(fraccRef, {
                configuracionRecibos: {
                    ...configRecibos,
                    firmaPermisoUrl: url
                }
            });
        } catch (error) {
            console.error('Error subiendo firma:', error);
            alert('Error al subir la firma. Por favor intenta nuevamente.');
        } finally {
            setUploadingFirma(false);
        }
    };

    const handleImagenReferenciaUpload = async (index, e) => {
        const file = e.target.files[0];
        if (!file || !fraccionamientoId) return;

        const newUploading = [...uploadingImagenes];
        newUploading[index] = true;
        setUploadingImagenes(newUploading);

        try {
            const fileRef = ref(storage, `fraccionamientos/${fraccionamientoId}/permisos/techado/referencia_${index}_${Date.now()}.${file.name.split('.').pop()}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            
            const newImagenes = [...imagenesReferencia];
            newImagenes[index] = url;
            setImagenesReferencia(newImagenes);
            
            // Guardar las imágenes en Firestore inmediatamente
            try {
                await guardarImagenesReferencia(newImagenes);
                console.log(`Imagen ${index + 1} guardada exitosamente en la configuración`);
            } catch (error) {
                console.error('Error guardando imagen en configuración:', error);
            }
        } catch (error) {
            console.error('Error subiendo imagen de referencia:', error);
            alert('Error al subir la imagen. Por favor intenta nuevamente.');
        } finally {
            const newUploading = [...uploadingImagenes];
            newUploading[index] = false;
            setUploadingImagenes(newUploading);
        }
    };

    const removeImagenReferencia = async (index) => {
        const newImagenes = [...imagenesReferencia];
        newImagenes[index] = null;
        setImagenesReferencia(newImagenes);
        
        // Guardar las imágenes actualizadas en Firestore
        await guardarImagenesReferencia(newImagenes);
    };
    
    const guardarImagenesReferencia = async (imagenes) => {
        if (!fraccionamientoId) return;
        try {
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccData = (await getDoc(fraccRef)).data() || {};
            const configRecibos = fraccData.configuracionRecibos || {};
            const configPermiso = configRecibos.configuracionPermiso || {};
            
            // Asegurar que el array tenga exactamente 4 elementos
            const imagenesParaGuardar = [...imagenes];
            while (imagenesParaGuardar.length < 4) {
                imagenesParaGuardar.push(null);
            }
            
            await updateDoc(fraccRef, {
                configuracionRecibos: {
                    ...configRecibos,
                    configuracionPermiso: {
                        ...configPermiso,
                        techado: {
                            ...configPermiso.techado,
                            imagenesReferencia: imagenesParaGuardar.slice(0, 4)
                        }
                    }
                }
            });
            console.log('Imágenes de referencia guardadas exitosamente');
        } catch (error) {
            console.error('Error guardando imágenes de referencia:', error);
            alert('Error al guardar las imágenes. Por favor intenta nuevamente.');
        }
    };

    const formatDate = (date, ciudadEstado) => {
        const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                       'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        const day = date.getDate().toString().padStart(2, '0');
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        return `${ciudadEstado} a ${day} de ${month} de ${year}.`;
    };

    const guardarConfiguracionPermiso = async () => {
        if (!fraccionamientoId) return;
        try {
            const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
            const fraccData = (await getDoc(fraccRef)).data() || {};
            const configRecibos = fraccData.configuracionRecibos || {};
            const configPermiso = configRecibos.configuracionPermiso || {};
            
            await updateDoc(fraccRef, {
                configuracionRecibos: {
                    ...configRecibos,
                    configuracionPermiso: {
                        ...configPermiso,
                        construccion: {
                            ...configPermiso.construccion,
                            redaccion: redaccionConstruccion
                        },
                        techado: {
                            ...configPermiso.techado,
                            redaccion: redaccionTechado
                        },
                        fecha: Timestamp.fromDate(fechaPermiso),
                        adminCargo: adminCargo
                    }
                }
            });
        } catch (error) {
            console.error('Error guardando configuración de permiso:', error);
        }
    };

    const handleFormatCommand = (command, value = null) => {
        document.execCommand(command, false, value);
    };

    const insertVariable = (variable) => {
        const editorContent = document.getElementById('editor-content-permiso');
        const formTextarea = document.getElementById('redaccion-textarea-permiso');
        
        if (editorContent && document.activeElement === editorContent) {
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode(`{${variable}}`);
                range.insertNode(textNode);
                range.setStartAfter(textNode);
                range.setEndAfter(textNode);
                selection.removeAllRanges();
                selection.addRange(range);
                const currentRedaccion = tipoPermiso === 'construccion' ? redaccionConstruccion : redaccionTechado;
                if (tipoPermiso === 'construccion') {
                    setRedaccionConstruccion(editorContent.textContent || editorContent.innerText);
                } else {
                    setRedaccionTechado(editorContent.textContent || editorContent.innerText);
                }
            } else {
                editorContent.textContent = (editorContent.textContent || '') + `{${variable}}`;
                if (tipoPermiso === 'construccion') {
                    setRedaccionConstruccion(editorContent.textContent);
                } else {
                    setRedaccionTechado(editorContent.textContent);
                }
            }
        } else if (formTextarea) {
            const start = formTextarea.selectionStart;
            const end = formTextarea.selectionEnd;
            const currentRedaccion = tipoPermiso === 'construccion' ? redaccionConstruccion : redaccionTechado;
            const newText = currentRedaccion.substring(0, start) + `{${variable}}` + currentRedaccion.substring(end);
            if (tipoPermiso === 'construccion') {
                setRedaccionConstruccion(newText);
            } else {
                setRedaccionTechado(newText);
            }
            setTimeout(() => {
                formTextarea.focus();
                const newPosition = start + variable.length + 2;
                formTextarea.setSelectionRange(newPosition, newPosition);
            }, 0);
        }
    };

    const generarPermiso = () => {
        if (!selectedCasa || !fraccionamientoData) return null;

        const fechaFormateada = formatDate(fechaPermiso, ciudadEstado);
        const residenteNombre = getResidentName(selectedCasa);
        const numeroCasa = selectedCasa.numero || 'N/A';
        const nombreFracc = fraccionamientoData.nombre || 'N/A';
        const direccion = direccionFraccionamiento || 'N/A';
        const admin = adminNombre || 'N/A';
        const cargo = adminCargo || 'Administradora del Fraccionamiento';

        // Usar la redacción correspondiente al tipo de permiso
        let textoCuerpo = tipoPermiso === 'construccion' ? redaccionConstruccion.trim() : redaccionTechado.trim();
        
        if (!textoCuerpo) {
            if (tipoPermiso === 'techado') {
                textoCuerpo = `Por medio de la presente se le otorga el permiso de instalación de techumbre al C. {residente}, propietario del condominio 2 casa {numeroCasa}, ubicado en la Unidad Condominal {nombreFracc}, {direccion}. 
Dicho permiso es exclusivamente para la instalación de techumbre permitida establecida en la Asamblea Plenaria celebrada el día 29 de septiembre de 2024, con las especificaciones exactas del prototipo aprobado. 
Los acabados deberán de ser del mismo tipo al prototipo aprobado para no romper con la armonía visual-arquitectónica del Condominio.    
Siendo las siguientes:

TECHUMBRE ESPECIFICACIONES:

MATERIAL
CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"
• PERFIL CUADRADO DE 1" CAL. 14 PARA SOPORTE
• ARMADO DE ESTRUCTURA PARA EL TECHADO, PERFIL PTR 3X2" CAL. 14
• UTILIZACIÓN DE PLASTI TEJA
• ÁNGULOS DE FIJACIÓN AL MURO DE SOPORTE.`;
            } else {
                textoCuerpo = `Por medio de la presente se le otorga el permiso de construcción al C. {residente}, propietario condominio 2 casa {numeroCasa}, ubicado en {nombreFracc}, {direccion}. Dicho permiso es exclusivamente para la construcción permitida en el reglamento protocolizado y vigente para el Condominio {nombreFracc}, en el Capítulo I; EN RELACIÓN A LAS UNIDADES EN CONDOMINIO, ARTÍCULO 7, PÁRRAFO 5, SEGUNDA PARTE: La única ampliación autorizada es la que corresponde al prototipo denominado Pera (vivienda de 2 recámaras y 2 baños completos) esta solo se podrá llevar a cabo al frente de la vivienda siguiendo los lineamientos del manual de ampliación específico para este prototipo. Los acabados (pintura, terminados, losas, tejas, etc) deberán de ser del mismo tipo de los utilizados en el resto de las casas para no romper con la armonía visual-arquitectónica del Condominio. Con el compromiso de cumplir debidamente con el convenio de pago por cuotas de mantenimiento vencidas.`;
            }
        }

        // Reemplazar variables en la redacción personalizada
        // Nota: {residente} ya incluye "C." en el texto, así que solo reemplazamos con el nombre
        textoCuerpo = textoCuerpo
            .replace(/\{nombreFracc\}/g, nombreFracc)
            .replace(/\{admin\}/g, admin)
            .replace(/\{numeroCasa\}/g, numeroCasa)
            .replace(/\{residente\}/g, residenteNombre)
            .replace(/\{direccion\}/g, direccion);

        // Formatear la sección de especificaciones de techumbre si existe y no está ya formateada
        if (tipoPermiso === 'techado' && textoCuerpo.includes('TECHUMBRE ESPECIFICACIONES:') && !textoCuerpo.includes('especificaciones-techumbre')) {
            // Detectar y formatear la sección de especificaciones con diferentes formatos
            const patrones = [
                // Patrón con saltos de línea y bullet points
                /TECHUMBRE ESPECIFICACIONES:\s*[\n\r]+\s*MATERIAL\s*[\n\r]+\s*CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"\s*[\n\r]+\s*[•·]\s*([^\n\r•·]+)\s*[\n\r]+\s*[•·]\s*([^\n\r•·]+)\s*[\n\r]+\s*[•·]\s*([^\n\r•·]+)\s*[\n\r]+\s*[•·]\s*([^\n\r•·]+)/i,
                // Patrón más flexible
                /TECHUMBRE ESPECIFICACIONES:[\s\S]*?MATERIAL[\s\S]*?CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"[\s\S]*?[•·]\s*([^\n\r•·]+)[\s\S]*?[•·]\s*([^\n\r•·]+)[\s\S]*?[•·]\s*([^\n\r•·]+)[\s\S]*?[•·]\s*([^\n\r•·]+)/i
            ];
            
            let formateado = false;
            for (const regex of patrones) {
                const match = textoCuerpo.match(regex);
                if (match) {
                    const items = [match[1].trim(), match[2].trim(), match[3].trim(), match[4].trim()];
                    const especificacionesHTML = `<div class="especificaciones-techumbre"><div class="especificaciones-contenido"><div class="especificaciones-titulo">TECHUMBRE ESPECIFICACIONES:</div><div class="especificaciones-material">MATERIAL</div><div class="especificaciones-considerando">CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"</div><ul class="especificaciones-lista"><li>${items[0]}</li><li>${items[1]}</li><li>${items[2]}</li><li>${items[3]}</li></ul></div></div>`;
                    textoCuerpo = textoCuerpo.replace(regex, especificacionesHTML);
                    formateado = true;
                    break;
                }
            }
            
            // Si no se encontró con regex, intentar formateo manual dividiendo el texto
            if (!formateado) {
                const partes = textoCuerpo.split('TECHUMBRE ESPECIFICACIONES:');
                if (partes.length > 1) {
                    const resto = partes[1];
                    const materialMatch = resto.match(/MATERIAL\s*[\n\r]+\s*CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"\s*[\n\r]+(.*)/is);
                    if (materialMatch) {
                        const itemsTexto = materialMatch[1];
                        const items = itemsTexto.split(/[•·]/).filter(item => item.trim()).slice(0, 4).map(item => item.trim());
                        if (items.length === 4) {
                            const especificacionesHTML = `<div class="especificaciones-techumbre"><div class="especificaciones-contenido"><div class="especificaciones-titulo">TECHUMBRE ESPECIFICACIONES:</div><div class="especificaciones-material">MATERIAL</div><div class="especificaciones-considerando">CONSIDERANDO EL TECHO A BASE DE "PLASTI TEJA"</div><ul class="especificaciones-lista"><li>${items[0]}</li><li>${items[1]}</li><li>${items[2]}</li><li>${items[3]}</li></ul></div></div>`;
                            const textoAntes = partes[0];
                            const textoDespues = resto.substring(materialMatch.index + materialMatch[0].length);
                            textoCuerpo = textoAntes + especificacionesHTML + textoDespues;
                        }
                    }
                }
            }
        }

        // Agregar imágenes justo antes del "Atentamente:" si es techado
        let textoCuerpoFinal = textoCuerpo;
        if (tipoPermiso === 'techado' && imagenesReferencia.some(img => img)) {
            const imagenesHTML = `<div class="cuerpo-imagenes">${imagenesReferencia.filter(img => img).map((img, idx) => `<img src="${img}" alt="Referencia ${idx + 1}" />`).join('')}</div>`;
            textoCuerpoFinal = textoCuerpo + imagenesHTML;
        }

        return {
            fecha: fechaFormateada,
            residente: residenteNombre,
            numeroCasa,
            nombreFracc,
            direccion,
            admin,
            cargo,
            titulo: tituloPermiso,
            textoCuerpo: textoCuerpoFinal
        };
    };

    const handlePrint = () => {
        const permiso = generarPermiso();
        if (!permiso) {
            alert('Por favor selecciona un residente primero.');
            return;
        }

        const printWindow = window.open('', '_blank');
        const imagenesReferenciaHTML = tipoPermiso === 'techado' && imagenesReferencia.some(img => img) 
            ? `<div style="display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px;">
                ${imagenesReferencia.map((img, idx) => 
                    img ? `<img src="${img}" alt="Referencia ${idx + 1}" style="max-width: 200px; max-height: 150px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px;" />` : ''
                ).join('')}
               </div>`
            : '';

        const htmlContent = `
            <!DOCTYPE html>
            <html lang="es">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>${permiso.titulo}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body {
                        font-family: 'Times New Roman', serif;
                        font-size: 12pt;
                        line-height: 1.6;
                        color: #000;
                        padding: 40px;
                        background: white;
                        max-width: 100%;
                        overflow-x: hidden;
                    }
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        margin-bottom: 30px;
                    }
                    .logo {
                        max-width: 150px;
                        max-height: 80px;
                        object-fit: contain;
                    }
                    .fecha {
                        text-align: right;
                        font-size: 11pt;
                    }
                    .titulo {
                        text-align: center;
                        font-size: 16pt;
                        font-weight: bold;
                        margin: 30px 0 20px 0;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                    }
                    .cuerpo {
                        text-align: justify;
                        margin: 20px 0;
                        line-height: 1.8;
                    }
                    .cuerpo-con-imagenes {
                        display: flex;
                        flex-direction: column;
                        gap: 20px;
                        margin: 20px 0;
                    }
                    .cuerpo-texto {
                        text-align: justify;
                        line-height: 1.8;
                    }
                    .especificaciones-techumbre {
                        margin: 20px 0;
                        max-width: 100%;
                        overflow: hidden;
                    }
                    .especificaciones-contenido {
                        width: 100%;
                        max-width: 100%;
                        overflow: hidden;
                    }
                    .especificaciones-titulo {
                        font-weight: bold;
                        font-size: 13pt;
                        margin-bottom: 10px;
                        text-transform: uppercase;
                    }
                    .especificaciones-material {
                        font-weight: 600;
                        font-size: 12pt;
                        margin: 10px 0 5px 0;
                        text-transform: uppercase;
                    }
                    .especificaciones-considerando {
                        font-size: 11pt;
                        margin: 5px 0 15px 0;
                        font-style: italic;
                    }
                    .especificaciones-lista {
                        margin: 10px 0 10px 20px;
                        padding-left: 20px;
                        list-style-type: disc;
                    }
                    .especificaciones-lista li {
                        margin: 8px 0;
                        line-height: 1.6;
                        font-size: 11pt;
                        padding-left: 5px;
                    }
                    .cuerpo-imagenes {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 6px;
                        padding: 7px;
                        border: 1px solid #d1d5db;
                        border-radius: 8px;
                        background: #f9fafb;
                        max-width: 238px;
                        width: 238px;
                        margin: 20px auto;
                        box-sizing: border-box;
                    }
                    .cuerpo-imagenes img {
                        width: 100%;
                        height: 92px;
                        object-fit: cover;
                        border: 1px solid #e5e7eb;
                        border-radius: 4px;
                        background: white;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                        display: block;
                    }
                    .firma-section {
                        margin-top: 40px;
                        text-align: center;
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                    }
                    .firma-img {
                        max-width: 360px;
                        max-height: 144px;
                        object-fit: contain;
                        margin: 0 auto 10px auto;
                        display: block;
                    }
                    .admin-nombre {
                        font-weight: bold;
                        margin-top: 5px;
                        text-align: center;
                        width: 100%;
                    }
                    .admin-cargo {
                        font-size: 10pt;
                        margin-top: 5px;
                        text-align: center;
                        width: 100%;
                    }
                    @media print {
                        body { padding: 20px; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="logo" />` : '<div></div>'}
                    <div class="fecha">${permiso.fecha}</div>
                </div>
                
                <div class="titulo">${permiso.titulo}</div>
                
                    <div class="cuerpo">
                        ${permiso.textoCuerpo.includes('<div class="especificaciones-techumbre">') 
                            ? permiso.textoCuerpo 
                            : permiso.textoCuerpo.replace(/\n/g, '<br />')}
                    </div>
                    
                    <div style="margin: 20px 0; text-align: center;">
                        Atentamente:
                    </div>
                
                <div class="firma-section">
                    ${firmaUrl ? `<img src="${firmaUrl}" alt="Firma" class="firma-img" />` : ''}
                    <div class="admin-nombre">C. ${permiso.admin}</div>
                    <div class="admin-cargo">${permiso.cargo}</div>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    const permiso = generarPermiso();

    return (
        <div className="permisos-page">
            <div className="permisos-header">
                <div className="permisos-header-icon">
                    <FaFileAlt />
                </div>
                <div>
                    <h2>Documentos/Permisos</h2>
                    <p>Genera permisos de construcción o techumbre/techado</p>
                </div>
            </div>

            <div className="permisos-tipo-selector">
                <button
                    type="button"
                    className={`permiso-tipo-btn ${tipoPermiso === 'construccion' ? 'active' : ''}`}
                    onClick={() => setTipoPermiso('construccion')}
                >
                    <FaHammer />
                    <span>Permiso de Construcción</span>
                </button>
                <button
                    type="button"
                    className={`permiso-tipo-btn ${tipoPermiso === 'techado' ? 'active' : ''}`}
                    onClick={() => setTipoPermiso('techado')}
                >
                    <FaHome />
                    <span>Permiso de Techumbre/Techado</span>
                </button>
            </div>

            <div className="permisos-content">
                <div className="permisos-top-section">
                    <div className="permisos-card">
                        <h3>Seleccionar Residente</h3>
                        <div className="search-bar">
                            <div className="search-input-wrapper">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar por número de casa o nombre del residente..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-message">Cargando residentes...</div>
                        ) : filteredCasas.length === 0 ? (
                            <div className="empty-message">
                                {searchTerm ? 'No se encontraron residentes con ese criterio' : 'No hay residentes registrados'}
                            </div>
                        ) : (
                            <div className="casas-list">
                                {filteredCasas.map(casa => (
                                    <div
                                        key={casa.id}
                                        className={`casa-item ${selectedCasa?.id === casa.id ? 'selected' : ''}`}
                                        onClick={() => handleCasaSelect(casa)}
                                    >
                                        <div className="casa-info">
                                            <span className="casa-numero">Casa {casa.numero}</span>
                                            <span className="casa-residente">{getResidentName(casa)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="permisos-card">
                        <h3>Configuración del Documento</h3>
                        
                        <div className="form-group">
                            <label>Nombre de la Administradora</label>
                            <input
                                type="text"
                                value={adminNombre}
                                onChange={(e) => setAdminNombre(e.target.value)}
                                placeholder="Nombre de la administradora"
                            />
                        </div>

                        <div className="form-group">
                            <label>Cargo de la Administradora</label>
                            <input
                                type="text"
                                value={adminCargo}
                                onChange={(e) => {
                                    setAdminCargo(e.target.value);
                                    guardarConfiguracionPermiso();
                                }}
                                placeholder="Administradora del Fraccionamiento"
                            />
                        </div>

                        <div className="form-group">
                            <label>Ciudad y Estado</label>
                            <input
                                type="text"
                                value={ciudadEstado}
                                onChange={(e) => setCiudadEstado(e.target.value)}
                                placeholder="Ej: San Juan del Río Querétaro"
                            />
                        </div>

                        <div className="form-group">
                            <label>Dirección del Fraccionamiento</label>
                            <input
                                type="text"
                                value={direccionFraccionamiento}
                                onChange={(e) => setDireccionFraccionamiento(e.target.value)}
                                placeholder="Dirección completa"
                            />
                        </div>

                        <div className="form-group">
                            <label>Fecha del Documento</label>
                            <input
                                type="date"
                                value={fechaPermiso.toISOString().split('T')[0]}
                                onChange={(e) => {
                                    setFechaPermiso(new Date(e.target.value));
                                    guardarConfiguracionPermiso();
                                }}
                            />
                        </div>

                        <div className="form-group">
                            <label>Título del Documento</label>
                            <input
                                type="text"
                                value={tituloPermiso}
                                onChange={(e) => {
                                    setTituloPermiso(e.target.value);
                                    guardarConfiguracionPermiso();
                                }}
                                placeholder="Ej: PERMISO DE CONSTRUCCIÓN"
                            />
                        </div>

                        <div className="form-group">
                            <label>Redacción del Documento</label>
                            <div className="redaccion-container">
                                <textarea
                                    id="redaccion-textarea-permiso"
                                    rows="6"
                                    value={tipoPermiso === 'construccion' ? redaccionConstruccion : redaccionTechado}
                                    onChange={(e) => {
                                        if (tipoPermiso === 'construccion') {
                                            setRedaccionConstruccion(e.target.value);
                                        } else {
                                            setRedaccionTechado(e.target.value);
                                        }
                                        guardarConfiguracionPermiso();
                                    }}
                                    onBlur={guardarConfiguracionPermiso}
                                    placeholder="Deje en blanco para usar la redacción por defecto. Use {nombreFracc}, {admin}, {numeroCasa}, {residente}, {direccion} como variables."
                                    className="redaccion-textarea"
                                />
                                <button
                                    type="button"
                                    className="expand-editor-btn"
                                    onClick={() => setEditorModalOpen(true)}
                                    title="Abrir editor expandido"
                                >
                                    <FaExpand /> Abrir Editor
                                </button>
                            </div>
                            <div className="variables-list">
                                <small style={{color: '#666', fontSize: '12px', marginTop: '5px', display: 'block', marginBottom: '8px'}}>
                                    Variables disponibles:
                                </small>
                                <div className="variables-buttons">
                                    {['nombreFracc', 'admin', 'numeroCasa', 'residente', 'direccion'].map(variable => (
                                        <button
                                            key={variable}
                                            type="button"
                                            className="variable-btn"
                                            onClick={() => insertVariable(variable)}
                                        >
                                            {'{'}{variable}{'}'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {tipoPermiso === 'techado' && (
                            <div className="form-group">
                                <label>Imágenes de Referencia de Materiales (4 imágenes)</label>
                                <div className="imagenes-referencia-grid">
                                    {[0, 1, 2, 3].map((index) => (
                                        <div key={index} className="imagen-referencia-item">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleImagenReferenciaUpload(index, e)}
                                                disabled={uploadingImagenes[index]}
                                                style={{ display: 'none' }}
                                                id={`imagen-ref-${index}`}
                                            />
                                            {imagenesReferencia[index] ? (
                                                <div className="imagen-preview">
                                                    <img src={imagenesReferencia[index]} alt={`Referencia ${index + 1}`} />
                                                    <button
                                                        type="button"
                                                        className="remove-imagen-btn"
                                                        onClick={() => removeImagenReferencia(index)}
                                                        title="Eliminar imagen"
                                                    >
                                                        <FaTimes />
                                                    </button>
                                                </div>
                                            ) : (
                                                <label htmlFor={`imagen-ref-${index}`} className="upload-imagen-btn">
                                                    {uploadingImagenes[index] ? 'Subiendo...' : `↑ Imagen ${index + 1}`}
                                                </label>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="upload-section">
                            <div className="upload-item">
                                <label className="upload-label">
                                    <FaUpload /> Cargar Firma de Administradora
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFirmaUpload}
                                    disabled={uploadingFirma}
                                    style={{ display: 'none' }}
                                    id="firma-upload"
                                />
                                <label htmlFor="firma-upload" className="upload-button">
                                    {uploadingFirma ? 'Subiendo...' : firmaUrl ? 'Firma cargada ✓' : 'Seleccionar archivo'}
                                </label>
                                {firmaUrl && (
                                    <button
                                        className="remove-button"
                                        onClick={() => setFirmaUrl('')}
                                        title="Eliminar firma"
                                    >
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="permisos-preview-section">
                    <div className="permisos-card preview-card">
                        <div className="preview-header">
                            <h3>Vista Previa</h3>
                            <button
                                className="print-button"
                                onClick={handlePrint}
                                disabled={!permiso}
                            >
                                <FaPrint /> Imprimir/Exportar
                            </button>
                        </div>

                        {permiso ? (
                            <div className="preview-content">
                                <div className="preview-document">
                                    <div className="preview-header-doc">
                                        {logoUrl && (
                                            <img src={logoUrl} alt="Logo" className="preview-logo" />
                                        )}
                                        <div className="preview-fecha">{permiso.fecha}</div>
                                    </div>
                                    
                                    <div className="preview-titulo">{permiso.titulo}</div>
                                    
                                    <div className="preview-cuerpo" dangerouslySetInnerHTML={{ __html: permiso.textoCuerpo }} />
                                    
                                    <div style={{ margin: '20px 0', textAlign: 'center' }}>
                                        Atentamente:
                                    </div>
                                    
                                    <div className="preview-firma-section">
                                        {firmaUrl && (
                                            <img src={firmaUrl} alt="Firma" className="preview-firma-img" style={{ margin: '0 auto' }} />
                                        )}
                                        <div className="preview-admin-nombre" style={{ textAlign: 'center' }}>C. {permiso.admin}</div>
                                        <div className="preview-admin-cargo" style={{ textAlign: 'center' }}>{permiso.cargo}</div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="preview-empty">
                                <p>Selecciona un residente para ver la vista previa</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal del Editor de Texto Enriquecido */}
            {editorModalOpen && (
                <div className="editor-modal-overlay" onClick={() => setEditorModalOpen(false)}>
                    <div className="editor-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="editor-modal-header">
                            <h3>Editor de Redacción - {tipoPermiso === 'construccion' ? 'Construcción' : 'Techado'}</h3>
                            <button
                                className="editor-close-btn"
                                onClick={() => setEditorModalOpen(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="editor-toolbar">
                            <div className="toolbar-group">
                                <label>Tamaño de Fuente:</label>
                                <select
                                    value={fontSize}
                                    onChange={(e) => {
                                        setFontSize(Number(e.target.value));
                                        guardarConfiguracionPermiso();
                                    }}
                                    className="font-size-select"
                                >
                                    <option value="10">10pt</option>
                                    <option value="11">11pt</option>
                                    <option value="12">12pt</option>
                                    <option value="14">14pt</option>
                                    <option value="16">16pt</option>
                                    <option value="18">18pt</option>
                                </select>
                            </div>

                            <div className="toolbar-group">
                                <label>Fuente:</label>
                                <select
                                    value={fontFamily}
                                    onChange={(e) => {
                                        setFontFamily(e.target.value);
                                        guardarConfiguracionPermiso();
                                    }}
                                    className="font-family-select"
                                >
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Courier New">Courier New</option>
                                </select>
                            </div>

                            <div className="toolbar-separator"></div>

                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('bold')}
                                title="Negrita"
                            >
                                <FaBold />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('italic')}
                                title="Cursiva"
                            >
                                <FaItalic />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('underline')}
                                title="Subrayado"
                            >
                                <FaUnderline />
                            </button>

                            <div className="toolbar-separator"></div>

                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('justifyLeft')}
                                title="Alinear Izquierda"
                            >
                                <FaAlignLeft />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('justifyCenter')}
                                title="Centrar"
                            >
                                <FaAlignCenter />
                            </button>
                            <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => handleFormatCommand('justifyRight')}
                                title="Alinear Derecha"
                            >
                                <FaAlignRight />
                            </button>
                        </div>

                        <div className="editor-textarea-container">
                            <div
                                id="editor-content-permiso"
                                contentEditable
                                onInput={(e) => {
                                    const content = e.target.textContent || e.target.innerText;
                                    if (tipoPermiso === 'construccion') {
                                        setRedaccionConstruccion(content);
                                    } else {
                                        setRedaccionTechado(content);
                                    }
                                }}
                                onBlur={guardarConfiguracionPermiso}
                                className="editor-textarea"
                                style={{
                                    fontSize: `${fontSize}pt`,
                                    fontFamily: fontFamily
                                }}
                                suppressContentEditableWarning={true}
                            >
                                {tipoPermiso === 'construccion' ? redaccionConstruccion : redaccionTechado}
                            </div>
                        </div>

                        <div className="editor-variables-section">
                            <label>Insertar Variable:</label>
                            <div className="editor-variables-buttons">
                                {['nombreFracc', 'admin', 'numeroCasa', 'residente', 'direccion'].map(variable => (
                                    <button
                                        key={variable}
                                        type="button"
                                        className="editor-variable-btn"
                                        onClick={() => insertVariable(variable)}
                                    >
                                        {'{'}{variable}{'}'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="editor-modal-footer">
                            <button
                                className="editor-save-btn"
                                onClick={() => {
                                    guardarConfiguracionPermiso();
                                    setEditorModalOpen(false);
                                }}
                            >
                                Guardar y Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PermisosDocumentos;
